import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Tooltip,
  Drawer,
  Descriptions,
  Statistic,
  Row,
  Col,
  Tree,
  DatePicker,
  Badge,
  Progress,
  Timeline,
  Empty,
  Divider,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  UserOutlined,
  TeamOutlined,
  PhoneOutlined,
  CrownOutlined,
  DownloadOutlined,
  ReloadOutlined,
  RiseOutlined,
  FallOutlined,
  HistoryOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import {
  fetchAgents,
  fetchAgentDetail,
  fetchAgentTeam,
  updateAgentLevel,
  updateAgentStatus,
  setFilters,
  setAgentPagination,
  clearError,
} from '../../features/team/teamSlice';
import { formatDateTime, formatAmount, formatNumber, maskPhone, getStatusColor, getStatusText } from '../../utils/index';

const { Option } = Select;
const { RangePicker } = DatePicker;

const AgentManagement = () => {
  const dispatch = useDispatch();
  const { agents, agentDetail, agentTeam, agentPagination, filters, isLoading, error } = useSelector((state) => state.team);
  
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [detailVisible, setDetailVisible] = useState(false);
  const [teamVisible, setTeamVisible] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [levelModalVisible, setLevelModalVisible] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [batchLevelModalVisible, setBatchLevelModalVisible] = useState(false);
  const [upgradeHistoryVisible, setUpgradeHistoryVisible] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState([]);

  useEffect(() => {
    dispatch(fetchAgents({ ...filters, ...agentPagination }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filters, agentPagination.current, agentPagination.pageSize]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);



  // 搜索处理
  const handleSearch = (values) => {
    dispatch(setFilters(values));
    dispatch(setAgentPagination({ current: 1 }));
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    dispatch(setFilters({}));
    dispatch(setAgentPagination({ current: 1 }));
  };

  // 刷新数据
  const handleRefresh = () => {
    dispatch(fetchAgents({ ...filters, ...agentPagination }));
    message.success('数据已刷新');
  };

  // 导出数据
  const handleExport = () => {
    if (agents.length === 0) {
      message.warning('暂无数据可导出');
      return;
    }

    const exportData = agents.map(agent => ({
      '代理ID': agent.id,
      '姓名': agent.name,
      '手机号': agent.phone,
      '邀请码': agent.inviteCode,
      '代理等级': `Lv.${agent.level}`,
      '团队规模': agent.teamSize || 0,
      '直属下级': agent.directMembers || 0,
      '团队充值': agent.teamRecharge || 0,
      '个人充值': agent.selfRecharge || 0,
      '返点收益': agent.totalCommission || 0,
      '状态': getStatusText(agent.status),
      '注册时间': formatDateTime(agent.createdAt),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '代理数据');
    XLSX.writeFile(wb, `代理数据_${new Date().getTime()}.xlsx`);
    message.success('导出成功');
  };

  // 批量调整等级
  const handleBatchUpdateLevel = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要调整的代理');
      return;
    }
    setBatchLevelModalVisible(true);
  };

  // 确认批量调整等级
  const handleConfirmBatchUpdateLevel = async (values) => {
    try {
      const promises = selectedRowKeys.map(agentId =>
        dispatch(updateAgentLevel({
          agentId,
          level: values.level,
        })).unwrap()
      );
      await Promise.all(promises);
      message.success(`成功调整 ${selectedRowKeys.length} 个代理的等级`);
      setBatchLevelModalVisible(false);
      setSelectedRowKeys([]);
      dispatch(fetchAgents({ ...filters, ...agentPagination }));
    } catch (error) {
      message.error('批量调整失败：' + error);
    }
  };

  // 表格列配置
  const columns = [
    {
      title: '代理ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      fixed: 'left',
    },
    {
      title: '代理信息',
      dataIndex: 'name',
      key: 'name',
      width: 240,
      fixed: 'left',
      render: (name, record) => (
        <div>
          <div>
            <UserOutlined style={{ marginRight: 4 }} />
            <strong>{name}</strong>
            <Tag color="blue" icon={<CrownOutlined />} style={{ marginLeft: 8 }}>
              Lv.{record.level}
            </Tag>
            {record.isTopPerformer && (
              <Tag color="gold" icon={<RiseOutlined />} style={{ marginLeft: 4 }}>
                优秀
              </Tag>
            )}
          </div>
          <div style={{ color: '#8c8c8c', fontSize: '12px', marginTop: 4 }}>
            {record.phone}
          </div>
          <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
            邀请码: {record.inviteCode}
          </div>
        </div>
      ),
    },
    {
      title: '团队规模',
      children: [
        {
          title: '总人数',
          dataIndex: 'teamSize',
          key: 'teamSize',
          width: 100,
          align: 'center',
          sorter: (a, b) => (a.teamSize || 0) - (b.teamSize || 0),
          render: (size) => formatNumber(size || 0),
        },
        {
          title: '直属',
          dataIndex: 'directMembers',
          key: 'directMembers',
          width: 80,
          align: 'center',
          sorter: (a, b) => (a.directMembers || 0) - (b.directMembers || 0),
          render: (count) => (
            <Badge
              count={count || 0}
              showZero
              style={{ backgroundColor: '#52c41a' }}
            />
          ),
        },
      ],
    },
    {
      title: '充值业绩',
      children: [
        {
          title: '团队充值',
          dataIndex: 'teamRecharge',
          key: 'teamRecharge',
          width: 130,
          align: 'right',
          sorter: (a, b) => (a.teamRecharge || 0) - (b.teamRecharge || 0),
          render: (amount, record) => (
            <div>
              <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
                {formatAmount(amount || 0)}
              </div>
              {record.rechargeGrowth && record.rechargeGrowth > 0 && (
                <div style={{ fontSize: '12px', color: '#52c41a' }}>
                  <RiseOutlined /> {record.rechargeGrowth}%
                </div>
              )}
              {record.rechargeGrowth && record.rechargeGrowth < 0 && (
                <div style={{ fontSize: '12px', color: '#ff4d4f' }}>
                  <FallOutlined /> {Math.abs(record.rechargeGrowth)}%
                </div>
              )}
            </div>
          ),
        },
        {
          title: '个人充值',
          dataIndex: 'selfRecharge',
          key: 'selfRecharge',
          width: 120,
          align: 'right',
          sorter: (a, b) => (a.selfRecharge || 0) - (b.selfRecharge || 0),
          render: (amount) => (
            <span style={{ color: '#722ed1' }}>
              {formatAmount(amount || 0)}
            </span>
          ),
        },
      ],
    },
    {
      title: '返点收益',
      dataIndex: 'totalCommission',
      key: 'totalCommission',
      width: 130,
      align: 'right',
      sorter: (a, b) => (a.totalCommission || 0) - (b.totalCommission || 0),
      render: (amount, record) => (
        <div>
          <div style={{ fontWeight: 'bold', color: '#52c41a', fontSize: '14px' }}>
            {formatAmount(amount || 0)}
          </div>
          {record.commissionRate && (
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
              费率: {record.commissionRate}%
            </div>
          )}
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      filters: [
        { text: '正常', value: 'active' },
        { text: '禁用', value: 'inactive' },
        { text: '冻结', value: 'frozen' },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (date) => formatDateTime(date),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
          <Tooltip title="查看团队">
            <Button
              type="text"
              icon={<TeamOutlined />}
              onClick={() => handleViewTeam(record)}
            />
          </Tooltip>
          <Tooltip title="调整等级">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleUpdateLevel(record)}
            />
          </Tooltip>
          <Tooltip title="升级记录">
            <Button
              type="text"
              icon={<HistoryOutlined />}
              onClick={() => handleViewUpgradeHistory(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 查看升级记录
  const handleViewUpgradeHistory = (agent) => {
    setSelectedAgent(agent);
    setUpgradeHistoryVisible(true);
  };

  // 查看详情
  const handleViewDetail = (agent) => {
    setSelectedAgent(agent);
    dispatch(fetchAgentDetail(agent.id));
    setDetailVisible(true);
  };

  // 查看团队
  const handleViewTeam = async (agent) => {
    setSelectedAgent(agent);
    setTeamVisible(true);
    
    try {
      // 后端已经返回完整的团队架构
      const result = await dispatch(fetchAgentTeam(agent.id)).unwrap();
      
      // 获取所有节点的key以展开所有节点
      const getAllKeys = (data) => {
        let keys = [];
        const traverse = (nodes) => {
          if (!nodes || !Array.isArray(nodes)) return;
          nodes.forEach(node => {
            keys.push(node.id);
            if (node.children && Array.isArray(node.children)) {
              traverse(node.children);
            }
          });
        };
        traverse(data);
        return keys;
      };
      
      const keys = getAllKeys(result);
      setExpandedKeys(keys);
    } catch (error) {
      console.error('加载团队失败:', error);
      message.error('加载团队结构失败');
    }
  };

  // 调整等级
  const handleUpdateLevel = (agent) => {
    setSelectedAgent(agent);
    form.setFieldsValue({ level: agent.level });
    setLevelModalVisible(true);
  };



  // 确认更新等级
  const handleConfirmUpdateLevel = async (values) => {
    try {
      await dispatch(updateAgentLevel({
        agentId: selectedAgent.id,
        level: values.level,
      })).unwrap();
      message.success('代理等级更新成功');
      setLevelModalVisible(false);
      dispatch(fetchAgents({ ...filters, ...agentPagination }));
    } catch (error) {
      message.error(error);
    }
  };

  // 确认更新状态
  const handleConfirmUpdateStatus = async (values) => {
    try {
      await dispatch(updateAgentStatus({
        agentId: selectedAgent.id,
        status: values.status,
      })).unwrap();
      message.success('代理状态更新成功');
      setStatusModalVisible(false);
      dispatch(fetchAgents({ ...filters, ...agentPagination }));
    } catch (error) {
      message.error(error);
    }
  };

  // 表格选择
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  // 分页处理
  const handleTableChange = (pagination) => {
    dispatch(setAgentPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  // 构建团队树数据
  const buildTeamTree = (teamData) => {
    // 确保 teamData 是数组
    if (!teamData) {
      return [];
    }
    
    // 如果 teamData 是对象且有 data 或 team 属性，提取数组
    let dataArray = teamData;
    if (!Array.isArray(teamData)) {
      dataArray = teamData.data || teamData.team || teamData.children || [];
    }
    
    // 如果仍然不是数组，返回空数组
    if (!Array.isArray(dataArray)) {
      return [];
    }
    
    const buildNode = (agent) => {
      return {
        title: (
          <Space>
            <UserOutlined />
            <span>{agent.name}</span>
            <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
              ({agent.phone})
            </span>
            <Tag color="blue">Lv.{agent.level}</Tag>
          </Space>
        ),
        key: agent.id,
        children: agent.children && Array.isArray(agent.children) ? agent.children.map(buildNode) : undefined,
      };
    };

    return dataArray.map(buildNode);
  };

  return (
    <div>
      <Card>
        {/* 搜索表单 */}
        <Form
          form={form}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="keyword">
            <Input
              placeholder="搜索代理姓名/手机号/邀请码"
              prefix={<SearchOutlined />}
              style={{ width: 240 }}
              allowClear
            />
          </Form.Item>
          <Form.Item name="level">
            <Select placeholder="代理等级" style={{ width: 120 }} allowClear>
              <Option value="1">Lv.1</Option>
              <Option value="2">Lv.2</Option>
              <Option value="3">Lv.3</Option>
              <Option value="4">Lv.4</Option>
              <Option value="5">Lv.5</Option>
            </Select>
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="代理状态" style={{ width: 120 }} allowClear>
              <Option value="active">正常</Option>
              <Option value="inactive">禁用</Option>
              <Option value="frozen">冻结</Option>
            </Select>
          </Form.Item>
          {filterVisible && (
            <>
              <Form.Item name="dateRange">
                <RangePicker placeholder={['注册开始日期', '注册结束日期']} />
              </Form.Item>
              <Form.Item name="minTeamSize">
                <Input
                  placeholder="最小团队规模"
                  type="number"
                  style={{ width: 140 }}
                />
              </Form.Item>
              <Form.Item name="minRecharge">
                <Input
                  placeholder="最小充值金额"
                  type="number"
                  style={{ width: 140 }}
                />
              </Form.Item>
            </>
          )}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 操作按钮 */}
        <div className="table-toolbar">
          <Space>
            <Button
              type="primary"
              disabled={selectedRowKeys.length === 0}
              icon={<EditOutlined />}
              onClick={handleBatchUpdateLevel}
            >
              批量调整等级 ({selectedRowKeys.length})
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
            >
              导出数据
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
            >
              刷新
            </Button>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFilterVisible(!filterVisible)}
            >
              {filterVisible ? '收起筛选' : '展开筛选'}
            </Button>
          </Space>
          <div>
            共 {agentPagination.total} 个代理
            {selectedRowKeys.length > 0 && (
              <span style={{ marginLeft: 8, color: '#1890ff' }}>
                已选择 {selectedRowKeys.length} 个
              </span>
            )}
          </div>
        </div>

        {/* 代理表格 */}
        <Table
          columns={columns}
          dataSource={agents}
          rowKey="id"
          loading={isLoading}
          rowSelection={rowSelection}
          pagination={{
            current: agentPagination.current,
            pageSize: agentPagination.pageSize,
            total: agentPagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
          bordered
        />
      </Card>

      {/* 代理详情抽屉 */}
      <Drawer
        title="代理详情"
        width={720}
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
      >
        {agentDetail && (
          <div>
            {/* 统计卡片 */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Statistic
                  title="代理等级"
                  value={agentDetail.level}
                  prefix={<CrownOutlined />}
                  suffix="级"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="团队规模"
                  value={agentDetail.teamSize || 0}
                  prefix={<TeamOutlined />}
                  suffix="人"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="直属下级"
                  value={agentDetail.directMembers || 0}
                  prefix={<UserOutlined />}
                  suffix="人"
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Col>
            </Row>

            {/* 业绩统计 */}
            <Card title="业绩统计" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="团队充值"
                    value={agentDetail.teamRecharge || 0}
                    prefix="¥"
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="个人充值"
                    value={agentDetail.selfRecharge || 0}
                    prefix="¥"
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Statistic
                    title="总充值"
                    value={(agentDetail.selfRecharge || 0) + (agentDetail.teamRecharge || 0)}
                    prefix="¥"
                    valueStyle={{ color: '#fa8c16', fontWeight: 'bold' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="返点收益"
                    value={agentDetail.totalCommission || 0}
                    prefix="¥"
                    valueStyle={{ color: '#52c41a', fontWeight: 'bold' }}
                  />
                </Col>
              </Row>
            </Card>

            {/* 基本信息 */}
            <Descriptions
              title="基本信息"
              bordered
              column={2}
              size="small"
            >
              <Descriptions.Item label="代理ID">
                {agentDetail.id}
              </Descriptions.Item>
              <Descriptions.Item label="姓名">
                {agentDetail.name}
              </Descriptions.Item>
              <Descriptions.Item label="手机号">
                <Space>
                  <PhoneOutlined />
                  {agentDetail.phone}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="邀请码">
                <Tag color="green">{agentDetail.inviteCode}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="代理等级">
                <Tag color="blue" icon={<CrownOutlined />}>Lv.{agentDetail.level}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="代理状态">
                <Tag color={getStatusColor(agentDetail.status)}>
                  {getStatusText(agentDetail.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="注册时间">
                {formatDateTime(agentDetail.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label="最后登录">
                {formatDateTime(agentDetail.lastLoginTime)}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Drawer>

      {/* 团队结构抽屉 */}
      <Drawer
        title="团队结构"
        width={800}
        open={teamVisible}
        onClose={() => setTeamVisible(false)}
      >
        {selectedAgent && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <h3>代理：{selectedAgent.name} ({selectedAgent.phone})</h3>
            </div>
            <Tree
              treeData={buildTeamTree(agentTeam)}
              expandedKeys={expandedKeys}
              onExpand={setExpandedKeys}
              showLine
            />
          </div>
        )}
      </Drawer>

      {/* 调整等级模态框 */}
      <Modal
        title="调整代理等级"
        open={levelModalVisible}
        onCancel={() => setLevelModalVisible(false)}
        footer={null}
      >
        <Form
          layout="vertical"
          onFinish={handleConfirmUpdateLevel}
        >
          <Form.Item label="代理信息">
            <Input value={`${selectedAgent?.name} (${selectedAgent?.phone})`} disabled />
          </Form.Item>
          <Form.Item
            label="新等级"
            name="level"
            rules={[{ required: true, message: '请选择新等级' }]}
          >
            <Select placeholder="请选择新等级">
              <Option value="1">Lv.1</Option>
              <Option value="2">Lv.2</Option>
              <Option value="3">Lv.3</Option>
              <Option value="4">Lv.4</Option>
              <Option value="5">Lv.5</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                确认调整
              </Button>
              <Button onClick={() => setLevelModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 调整状态模态框 */}
      <Modal
        title="调整代理状态"
        open={statusModalVisible}
        onCancel={() => setStatusModalVisible(false)}
        footer={null}
      >
        <Form
          layout="vertical"
          onFinish={handleConfirmUpdateStatus}
        >
          <Form.Item label="代理信息">
            <Input value={`${selectedAgent?.name} (${selectedAgent?.phone})`} disabled />
          </Form.Item>
          <Form.Item
            label="新状态"
            name="status"
            rules={[{ required: true, message: '请选择新状态' }]}
          >
            <Select placeholder="请选择新状态">
              <Option value="active">正常</Option>
              <Option value="inactive">禁用</Option>
              <Option value="frozen">冻结</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                确认调整
              </Button>
              <Button onClick={() => setStatusModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量调整等级模态框 */}
      <Modal
        title={`批量调整代理等级 (${selectedRowKeys.length}个)`}
        open={batchLevelModalVisible}
        onCancel={() => setBatchLevelModalVisible(false)}
        footer={null}
      >
        <Form
          layout="vertical"
          onFinish={handleConfirmBatchUpdateLevel}
        >
          <Form.Item label="选中的代理">
            <div style={{ maxHeight: 200, overflow: 'auto', padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
              {selectedRowKeys.map(id => {
                const agent = agents.find(a => a.id === id);
                return agent ? (
                  <Tag key={id} style={{ marginBottom: 4 }}>
                    {agent.name} (Lv.{agent.level})
                  </Tag>
                ) : null;
              })}
            </div>
          </Form.Item>
          <Form.Item
            label="新等级"
            name="level"
            rules={[{ required: true, message: '请选择新等级' }]}
          >
            <Select placeholder="请选择新等级">
              <Option value="1">Lv.1</Option>
              <Option value="2">Lv.2</Option>
              <Option value="3">Lv.3</Option>
              <Option value="4">Lv.4</Option>
              <Option value="5">Lv.5</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                确认调整
              </Button>
              <Button onClick={() => setBatchLevelModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 升级记录抽屉 */}
      <Drawer
        title="代理升级记录"
        width={600}
        open={upgradeHistoryVisible}
        onClose={() => setUpgradeHistoryVisible(false)}
      >
        {selectedAgent && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <h3>{selectedAgent.name} ({selectedAgent.phone})</h3>
              <Tag color="blue" icon={<CrownOutlined />}>
                当前等级: Lv.{selectedAgent.level}
              </Tag>
            </div>
            <Divider />
            {selectedAgent.upgradeHistory && selectedAgent.upgradeHistory.length > 0 ? (
              <Timeline>
                {selectedAgent.upgradeHistory.map((record, index) => (
                  <Timeline.Item
                    key={index}
                    color={record.type === 'upgrade' ? 'green' : 'blue'}
                  >
                    <div>
                      <div style={{ fontWeight: 'bold' }}>
                        {record.type === 'upgrade' ? '升级' : '调整'}: 
                        Lv.{record.fromLevel} → Lv.{record.toLevel}
                      </div>
                      <div style={{ color: '#8c8c8c', fontSize: '12px', marginTop: 4 }}>
                        {formatDateTime(record.createdAt)}
                      </div>
                      {record.reason && (
                        <div style={{ marginTop: 4 }}>
                          原因: {record.reason}
                        </div>
                      )}
                      {record.operator && (
                        <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
                          操作人: {record.operator}
                        </div>
                      )}
                    </div>
                  </Timeline.Item>
                ))}
              </Timeline>
            ) : (
              <Empty description="暂无升级记录" />
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default AgentManagement;
